import { useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import styles from "./PlacesAutocomplete.module.css";

interface PlacePrediction {
	description: string;
	place_id: string;
}

interface PlacesAutocompleteProps {
	id?: string;
	placeholder?: string;
	value: string;
	onChange: (value: string) => void;
	variant?: "default" | "onboarding";
	error?: boolean;
	className?: string;
}

export function PlacesAutocomplete({
	id,
	placeholder = "Enter location",
	value,
	onChange,
	variant = "default",
	error = false,
	className = "",
}: PlacesAutocompleteProps) {
	const [localValue, setLocalValue] = useState(value);
	const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const debounceTimer = useRef<NodeJS.Timeout | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	// Update local value when prop changes
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	// Close suggestions when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setShowSuggestions(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const searchPlaces = async (input: string) => {
		if (!input.trim()) {
			setSuggestions([]);
			setShowSuggestions(false);
			return;
		}

		setIsLoading(true);

		try {
			const endpoint = `/api/places/search?q=${encodeURIComponent(input)}`;
			const response = await api.get(endpoint);

			if (response.data.predictions && response.data.predictions.length > 0) {
				setSuggestions(response.data.predictions);
				setShowSuggestions(true);
			} else {
				setSuggestions([]);
				setShowSuggestions(false);
			}
		} catch (error) {
			console.warn("Places autocomplete not available:", error);
			setSuggestions([]);
			setShowSuggestions(false);
		} finally {
			setIsLoading(false);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setLocalValue(newValue);
		onChange(newValue);

		// Clear previous debounce timer
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
		}

		// Set up new debounced search
		debounceTimer.current = setTimeout(() => {
			searchPlaces(newValue);
		}, 300);
	};

	const handleSuggestionClick = (suggestion: PlacePrediction) => {
		setLocalValue(suggestion.description);
		onChange(suggestion.description);
		setShowSuggestions(false);
		setSuggestions([]);
	};

	return (
		<div ref={containerRef} className={styles.container}>
			<input
				id={id}
				type="text"
				placeholder={placeholder}
				value={localValue}
				onChange={handleChange}
				className={`${styles.input} ${styles[variant]} ${error ? styles.error : ""} ${className}`}
				autoComplete="off"
			/>
			{showSuggestions && suggestions.length > 0 && (
				<div className={styles.suggestions}>
					{suggestions.map((suggestion) => (
						<button
							key={suggestion.place_id}
							type="button"
							className={styles.suggestion}
							onClick={() => handleSuggestionClick(suggestion)}
						>
							{suggestion.description}
						</button>
					))}
				</div>
			)}
			{isLoading && <div className={styles.loading}>Searching...</div>}
		</div>
	);
}
